import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, FileText, Activity, KeyRound, BarChart3, Share2, LogOut, Plus, Download, Copy, Trash2, RotateCcw, Loader2, Menu, ExternalLink, Calendar } from "lucide-react";
import { Logo } from "../../components/Logo";
import { useAuth } from "../../lib/auth";
import { api, API } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quoted", "Inspected", "Purchased", "Lost"];
const STATUS_COLOR = { New: "bg-blue-100 text-blue-700", Contacted: "bg-amber-100 text-amber-700", Quoted: "bg-purple-100 text-purple-700", Inspected: "bg-cyan-100 text-cyan-700", Purchased: "bg-green-100 text-green-700", Lost: "bg-gray-200 text-gray-600" };
const SCOPES = ["content", "seo", "leads", "media", "analytics", "social", "settings"];

const Card = ({ children, className = "" }) => <div className={`rounded-2xl border border-brand-line bg-white ${className}`}>{children}</div>;

// ---------------- Overview ----------------
function Overview({ go }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  const cards = [
    { label: "Total leads", val: s?.leads_total, tab: "leads" },
    { label: "New leads", val: s?.leads_new, tab: "leads" },
    { label: "Purchased", val: s?.leads_won, tab: "leads" },
    { label: "Enquiries", val: s?.submissions, tab: "leads" },
  ];
  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">Overview</h1>
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button key={c.label} onClick={() => go(c.tab)} className="text-left"><Card className="p-5 hover:-translate-y-0.5 hover:shadow-md transition-all">
            <p className="text-sm font-semibold text-brand-muted">{c.label}</p>
            <p className="mt-2 font-display text-3xl font-black text-brand-navy">{c.val ?? "—"}</p>
          </Card></button>
        ))}
      </div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <Card className="p-6"><h3 className="font-display font-extrabold text-brand-navy">Content</h3><p className="mt-1 text-sm text-brand-muted">{s?.blog_posts ?? 0} blog posts · {s?.suburbs ?? 0} suburb pages</p><button onClick={() => go("content")} className="mt-4 text-sm font-bold text-brand-navy hover:text-brand-gold">Manage content →</button></Card>
        <Card className="p-6"><h3 className="font-display font-extrabold text-brand-navy">Agent API</h3><p className="mt-1 text-sm text-brand-muted">Give Wingman scoped access via API keys.</p><a href={`${API}/docs`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-brand-navy hover:text-brand-gold">Open API docs <ExternalLink className="h-3 w-3" /></a></Card>
      </div>
    </div>
  );
}

// ---------------- Leads ----------------
function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (filter) params.status = filter;
    api.get("/admin/leads", { params }).then((r) => setLeads(r.data)).finally(() => setLoading(false));
  }, [q, filter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
    if (sel?.id === id) setSel({ ...sel, status });
  };
  const addNote = async () => {
    if (!note.trim()) return;
    const { data } = await api.patch(`/admin/leads/${sel.id}`, { note });
    setSel(data); setNote(""); toast.success("Note added");
  };
  const exportCsv = () => window.open(`${API}/admin/leads/export.csv`, "_blank");

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-extrabold text-brand-navy">Leads inbox</h1>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border-2 border-brand-line px-4 py-2 text-sm font-bold text-brand-navy hover:bg-brand-surface" data-testid="export-csv-btn"><Download className="h-4 w-4" /> Export CSV</button>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, car..." className="flex-1 min-w-[200px] rounded-full border-2 border-brand-line px-4 py-2 text-sm outline-none focus:border-brand-gold" data-testid="lead-search" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-full border-2 border-brand-line px-4 py-2 text-sm outline-none" data-testid="lead-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <Card className="mt-4 overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-navy" /></div> :
          leads.length === 0 ? <p className="py-16 text-center text-brand-muted">No leads yet.</p> :
            <div className="divide-y divide-brand-line" data-testid="leads-list">
              {leads.map((l) => (
                <button key={l.id} onClick={() => setSel(l)} className="w-full text-left px-5 py-4 hover:bg-brand-surface transition-colors flex items-center justify-between gap-4" data-testid={`lead-row-${l.id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-brand-navy truncate">{l.name} · {l.year} {l.brand} {l.model}</p>
                    <p className="text-sm text-brand-muted truncate">{l.phone} · {l.suburb || "—"} · {new Date(l.created_at).toLocaleDateString("en-AU")}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLOR[l.status]}`}>{l.status}</span>
                </button>
              ))}
            </div>}
      </Card>

      {sel && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex justify-end" onClick={() => setSel(null)} data-testid="lead-drawer">
          <div className="w-full max-w-md h-full bg-white overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-extrabold text-brand-navy">{sel.name}</h2>
              <button onClick={() => setSel(null)} className="text-brand-muted">✕</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(sel.id, s)} className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${sel.status === s ? STATUS_COLOR[s] + " ring-2 ring-offset-1 ring-brand-navy" : "bg-brand-surface text-brand-muted hover:bg-brand-line"}`} data-testid={`set-status-${s}`}>{s}</button>
              ))}
            </div>
            <dl className="mt-6 space-y-2 text-sm">
              {[["Phone", sel.phone], ["Email", sel.email], ["Suburb", sel.suburb], ["Preferred time", sel.preferred_time], ["Vehicle", `${sel.year} ${sel.brand} ${sel.model}`], ["Rego", sel.rego], ["Kilometres", sel.kilometres], ["On finance", sel.on_finance], ["Accident", sel.accident], ["Written-off", sel.written_off], ["Condition", sel.condition], ["Log book", sel.service_logbook], ["Service history", sel.service_history], ["Source", sel.source]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-brand-line py-1.5"><dt className="text-brand-muted">{k}</dt><dd className="font-semibold text-brand-navy text-right">{v || "—"}</dd></div>
              ))}
            </dl>
            {sel.photos?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {sel.photos.map((p) => <img key={p} src={`${process.env.REACT_APP_BACKEND_URL}${p}`} alt="Car" className="h-20 w-20 rounded-lg object-cover border border-brand-line" />)}
              </div>
            )}
            <div className="mt-6">
              <h3 className="font-bold text-brand-navy">Notes</h3>
              <div className="mt-2 space-y-2">
                {(sel.notes || []).map((n, i) => <div key={n.at || i} className="rounded-lg bg-brand-surface p-3 text-sm"><p className="text-brand-navy">{n.text}</p><p className="mt-1 text-xs text-brand-muted">{n.by} · {new Date(n.at).toLocaleString("en-AU")}</p></div>)}
              </div>
              <div className="mt-3 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note..." className="flex-1 rounded-full border-2 border-brand-line px-4 py-2 text-sm outline-none focus:border-brand-gold" data-testid="note-input" />
                <button onClick={addNote} className="rounded-full bg-brand-navy px-4 py-2 text-sm font-bold text-white" data-testid="add-note-btn">Add</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Content ----------------
const COLLECTIONS = [
  { key: "blog_posts", label: "Blog Posts", fields: ["title", "slug", "excerpt"] },
  { key: "suburbs", label: "Suburb Pages", fields: ["name", "slug", "intro"] },
  { key: "testimonials", label: "Testimonials", fields: ["name", "location", "text"] },
  { key: "faqs", label: "FAQs", fields: ["question", "answer"] },
  { key: "services", label: "Service Pages", fields: ["nav_label", "slug", "subtitle"] },
];

function Content() {
  const [active, setActive] = useState("blog_posts");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/manage/${active}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [active]);
  useEffect(() => { load(); }, [load]);

  const cfg = COLLECTIONS.find((c) => c.key === active);
  const publish = async (id) => { await api.post(`/manage/${active}/${id}/publish`); toast.success("Published"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/manage/${active}/${id}`); toast.success("Deleted"); load(); };

  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">Content editor</h1>
      <p className="mt-1 text-sm text-brand-muted">Manage via the dashboard or let Wingman edit through the API. Every change is logged with rollback.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {COLLECTIONS.map((c) => (
          <button key={c.key} onClick={() => setActive(c.key)} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${active === c.key ? "bg-brand-navy text-white" : "bg-brand-surface text-brand-navy hover:bg-brand-line"}`} data-testid={`content-tab-${c.key}`}>{c.label}</button>
        ))}
      </div>
      <Card className="mt-4 overflow-hidden">
        {loading ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-navy" /></div> :
          <div className="divide-y divide-brand-line" data-testid="content-list">
            {items.map((it) => (
              <div key={it.id} className="px-5 py-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-bold text-brand-navy truncate">{it[cfg.fields[0]]}</p>
                  <p className="text-sm text-brand-muted truncate">{it[cfg.fields[2]] || it[cfg.fields[1]] || ""}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${it.status === "published" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{it.status || "published"}</span>
                  {it.status !== "published" && <button onClick={() => publish(it.id)} className="text-xs font-bold text-brand-navy hover:text-brand-gold" data-testid={`publish-${it.id}`}>Publish</button>}
                  <button onClick={() => del(it.id)} className="text-brand-muted hover:text-red-600" data-testid={`delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="py-16 text-center text-brand-muted">No items.</p>}
          </div>}
      </Card>
    </div>
  );
}

// ---------------- API Keys ----------------
function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([...SCOPES]);
  const [newKey, setNewKey] = useState(null);
  const load = () => api.get("/admin/api-keys").then((r) => setKeys(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name the key"); return; }
    const { data } = await api.post("/admin/api-keys", { name, scopes });
    setNewKey(data); setName(""); load();
  };
  const revoke = async (id) => { if (!window.confirm("Revoke this key?")) return; await api.delete(`/admin/api-keys/${id}`); toast.success("Revoked"); load(); };
  const toggle = (s) => setScopes((v) => v.includes(s) ? v.filter((x) => x !== s) : [...v, s]);

  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">API keys for Wingman</h1>
      <p className="mt-1 text-sm text-brand-muted">Scoped bearer tokens for the agent. Keys are shown once — copy immediately.</p>

      <Card className="mt-4 p-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]"><label className="text-xs font-bold uppercase text-brand-muted">Key name</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Wingman production" className="mt-1 w-full rounded-full border-2 border-brand-line px-4 py-2 text-sm outline-none focus:border-brand-gold" data-testid="key-name-input" /></div>
          <button onClick={create} className="inline-flex items-center gap-2 rounded-full bg-brand-navy px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-navy2" data-testid="create-key-btn"><Plus className="h-4 w-4" /> Generate key</button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {SCOPES.map((s) => <button key={s} onClick={() => toggle(s)} className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${scopes.includes(s) ? "bg-brand-gold text-brand-navy" : "bg-brand-surface text-brand-muted"}`} data-testid={`scope-${s}`}>{s}</button>)}
        </div>
      </Card>

      {newKey && (
        <Card className="mt-4 p-5 border-2 border-brand-gold" data-testid="new-key-banner">
          <p className="text-sm font-bold text-brand-navy">Copy your key now — it won't be shown again.</p>
          <div className="mt-2 flex gap-2">
            <code className="flex-1 rounded-lg bg-brand-surface px-3 py-2 text-xs break-all">{newKey.raw_key}</code>
            <button onClick={() => { navigator.clipboard.writeText(newKey.raw_key); toast.success("Copied"); }} className="rounded-lg bg-brand-navy px-3 text-white" data-testid="copy-key-btn"><Copy className="h-4 w-4" /></button>
          </div>
        </Card>
      )}

      <Card className="mt-4 overflow-hidden">
        <div className="divide-y divide-brand-line" data-testid="keys-list">
          {keys.map((k) => (
            <div key={k.id} className="px-5 py-4 flex items-center justify-between gap-4">
              <div><p className="font-bold text-brand-navy">{k.name} {!k.active && <span className="text-xs text-red-600">(revoked)</span>}</p><p className="text-sm text-brand-muted">{k.prefix}··· · {(k.scopes || []).join(", ")}</p></div>
              {k.active && <button onClick={() => revoke(k.id)} className="text-brand-muted hover:text-red-600" data-testid={`revoke-${k.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
          {keys.length === 0 && <p className="py-16 text-center text-brand-muted">No API keys yet.</p>}
        </div>
      </Card>
    </div>
  );
}

// ---------------- Activity ----------------
function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const load = () => api.get("/admin/audit").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);
  const rollback = async (id) => { if (!window.confirm("Roll back this change?")) return; try { await api.post(`/admin/audit/${id}/rollback`); toast.success("Rolled back"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Cannot roll back"); } };
  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">Agent activity log</h1>
      <p className="mt-1 text-sm text-brand-muted">Every change, who made it, and one-click rollback.</p>
      <Card className="mt-4 overflow-hidden">
        <div className="divide-y divide-brand-line" data-testid="audit-list">
          {logs.map((l) => (
            <div key={l.id} className="px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0"><p className="font-bold text-brand-navy truncate"><span className="uppercase text-xs bg-brand-surface rounded px-1.5 py-0.5 mr-2">{l.action}</span>{l.entity} {l.entity_id ? `· ${String(l.entity_id).slice(0, 8)}` : ""}</p><p className="text-sm text-brand-muted">{l.actor} ({l.actor_type}) · {new Date(l.created_at).toLocaleString("en-AU")}</p></div>
              {["update", "create"].includes(l.action) && <button onClick={() => rollback(l.id)} className="inline-flex items-center gap-1 text-xs font-bold text-brand-navy hover:text-brand-gold shrink-0" data-testid={`rollback-${l.id}`}><RotateCcw className="h-3 w-3" /> Roll back</button>}
            </div>
          ))}
          {logs.length === 0 && <p className="py-16 text-center text-brand-muted">No activity yet.</p>}
        </div>
      </Card>
    </div>
  );
}

// ---------------- Analytics ----------------
function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics/summary").then((r) => setA(r.data)).catch(() => {}); }, []);
  if (!a) return <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-navy" /></div>;
  const stats = [["Page views", a.page_views], ["Form starts", a.form_starts], ["Form completes", a.form_completes], ["Conversion", `${a.conversion_rate}%`], ["Phone taps", a.phone_taps], ["Email taps", a.email_taps]];
  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">Analytics</h1>
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map(([k, v]) => <Card key={k} className="p-5"><p className="text-sm font-semibold text-brand-muted">{k}</p><p className="mt-1 font-display text-3xl font-black text-brand-navy">{v}</p></Card>)}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card className="p-5"><h3 className="font-bold text-brand-navy">Quote wizard drop-off</h3>
          <div className="mt-3 space-y-2">
            {[["Step 1 → 2", a.step_dropoff?.["2"] || 0], ["Step 2 → 3", a.step_dropoff?.["3"] || 0], ["Completed", a.form_completes]].map(([k, v]) => (
              <div key={k}><div className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-bold">{v}</span></div><div className="mt-1 h-2 rounded-full bg-brand-surface"><div className="h-2 rounded-full bg-brand-gold" style={{ width: `${a.form_starts ? Math.min(100, (v / a.form_starts) * 100) : 0}%` }} /></div></div>
            ))}
          </div>
        </Card>
        <Card className="p-5"><h3 className="font-bold text-brand-navy">Lead sources</h3>
          <div className="mt-3 space-y-2">{(a.lead_sources || []).map((s) => <div key={s.source} className="flex justify-between text-sm border-b border-brand-line py-1.5"><span className="text-brand-muted">{s.source || "unknown"}</span><span className="font-bold">{s.count}</span></div>)}{(!a.lead_sources || a.lead_sources.length === 0) && <p className="text-sm text-brand-muted">No leads yet.</p>}</div>
        </Card>
      </div>
    </div>
  );
}

// ---------------- Social ----------------
function Social() {
  const [status, setStatus] = useState({});
  useEffect(() => { api.get("/social/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="font-display text-2xl font-extrabold text-brand-navy">Social calendar</h1>
      <p className="mt-1 text-sm text-brand-muted">Connect platforms to schedule posts. Website content and social posts appear here together.</p>
      <Card className="mt-4 p-6 flex items-center gap-3 text-brand-muted"><Calendar className="h-6 w-6 text-brand-gold" /> No scheduled items yet.</Card>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Object.entries(status).map(([p, v]) => (
          <Card key={p} className="p-5"><div className="flex items-center justify-between"><p className="font-bold text-brand-navy capitalize">{p}</p><span className="rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-bold">Not connected</span></div><p className="mt-2 text-sm text-brand-muted">{v.message}</p></Card>
        ))}
      </div>
    </div>
  );
}

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard, C: Overview },
  { key: "leads", label: "Leads", icon: Inbox, C: Leads },
  { key: "content", label: "Content", icon: FileText, C: Content },
  { key: "social", label: "Social", icon: Share2, C: Social },
  { key: "analytics", label: "Analytics", icon: BarChart3, C: Analytics },
  { key: "activity", label: "Activity", icon: Activity, C: ActivityLog },
  { key: "keys", label: "API Keys", icon: KeyRound, C: ApiKeys },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [open, setOpen] = useState(false);
  const Active = TABS.find((t) => t.key === tab).C;

  const doLogout = async () => { await logout(); navigate("/admin", { replace: true }); };

  return (
    <div className="min-h-screen bg-brand-surface flex">
      {/* sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-brand-line p-4 flex-col transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 lg:flex`} data-testid="admin-sidebar">
        <div className="px-2"><Logo /></div>
        <nav className="mt-6 space-y-1 flex-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); setOpen(false); }} className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold transition-colors ${tab === t.key ? "bg-brand-navy text-white" : "text-brand-navy hover:bg-brand-surface"}`} data-testid={`tab-${t.key}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-brand-muted hover:bg-brand-surface" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-20 bg-white border-b border-brand-line px-4 h-14 flex items-center justify-between">
          <button onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="h-6 w-6 text-brand-navy" /></button>
          <Logo />
          <div className="w-6" />
        </header>
        <div className="hidden lg:flex items-center justify-end gap-3 px-8 h-16 border-b border-brand-line bg-white">
          <span className="text-sm text-brand-muted">{user?.email}</span>
          {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />}
        </div>
        <main className="p-4 sm:p-8 max-w-6xl"><Active go={setTab} /></main>
      </div>
    </div>
  );
}
